import { score100 } from "./scoreDisplay";
import { REPORT_FOREST_IMAGE } from "./reportCoverAsset";
import { jsPDF } from "jspdf";
import {
  ESGE_COLORS,
  HIDDEN_FROM_TABLE_SUBSTAGES,
  PARAM_AGG,
  PARAM_KEYS,
  PARAM_UNIT_SHORT,
  PARAM_UNITS,
  ADVANCED_PL as PL, coverageDescription, advancedUnit, evidenceSummary, stageScoreNote, processBBreakdown,
  PL_SHORT,
  STAGES_ORDER,
  STAGE_FULL,
  STAGE_SHORT,
  aggregateStageInputs,
  buildCountryInferredRows,
  buildParamsByStage,
  criticalityLadder,
  heatColor,
  paramDominance,
  primaryPollutionDriver,
  productBuckets,
  rootCauses,
  top5Actions,
  variabilityByStage,
} from "./reportUtils";

/*
 * The advanced report is drawn directly with jsPDF. It does not capture the
 * page DOM, use canvas screenshots, or inherit responsive browser geometry.
 * Every page, cell, badge and chart therefore has deterministic coordinates.
 */

const PAGE = { w: 210, h: 297, left: 12, right: 198, top: 12, bottom: 281 };
const COLORS = {
  ink: "#303633",
  teal: "#18543B",
  aqua: "#729780",
  mint: "#EAF7F4",
  line: "#D9E5E2",
  soft: "#F5F9F8",
  muted: "#626B65",
  white: "#FFFFFF",
  red: "#D32F2F",
  redSoft: "#FFF2F2",
  orange: "#F57F17",
  orangeSoft: "#FFF3E0",
  green: "#2E7D32",
  greenDark: "#1B5E20",
  greenSoft: "#E8F5E9",
  blue: "#1976D2",
  purple: "#7B1FA2",
};

const SUBSTAGES_ORDER = [
  "raw_material_extraction", "raw_material_processing",
  "manufacturing_pre_processing", "manufacturing_production",
  "use_phase_operation", "use_phase_maintenance",
  "eol_recycling", "eol_recovery", "eol_disposal",
  "logistics_transportation", "logistics_storage",
];

const SS_SHORT = {
  raw_material_extraction: "RM Ext.", raw_material_processing: "RM Proc.",
  manufacturing_pre_processing: "Mfg Pre", manufacturing_production: "Mfg Prod",
  use_phase_operation: "Use Op.", use_phase_maintenance: "Use Maint.",
  eol_recycling: "EOL Recycle", eol_recovery: "EOL Recovery", eol_disposal: "EOL Disposal",
  logistics_transportation: "Transport", logistics_storage: "Storage",
};

const SS_FULL = {
  raw_material_extraction: "Raw Material - Extraction",
  raw_material_processing: "Raw Material - Processing",
  manufacturing_pre_processing: "Manufacturing - Pre-Processing",
  manufacturing_production: "Manufacturing - Production",
  use_phase_operation: "Use Phase - Operation",
  use_phase_maintenance: "Use Phase - Maintenance",
  eol_recycling: "End-of-Life - Recycling",
  eol_recovery: "End-of-Life - Recovery",
  eol_disposal: "End-of-Life - Disposal",
  logistics_transportation: "Supply Chain - Transportation",
  logistics_storage: "Supply Chain - Storage",
};

const SS_STAGE = {
  raw_material_extraction: "Raw Material", raw_material_processing: "Raw Material",
  manufacturing_pre_processing: "Manufacturing", manufacturing_production: "Manufacturing",
  use_phase_operation: "Use Phase", use_phase_maintenance: "Use Phase",
  eol_recycling: "End-of-Life", eol_recovery: "End-of-Life", eol_disposal: "End-of-Life",
  logistics_transportation: "Supply Chain", logistics_storage: "Supply Chain",
};

const SS_STAGE_KEY = {
  raw_material_extraction: "raw_material", raw_material_processing: "raw_material",
  manufacturing_pre_processing: "manufacturing", manufacturing_production: "manufacturing",
  use_phase_operation: "use_phase", use_phase_maintenance: "use_phase",
  eol_recycling: "end_of_life", eol_recovery: "end_of_life", eol_disposal: "end_of_life",
  logistics_transportation: "logistics", logistics_storage: "logistics",
};

const ascii = (value) => String(value ?? "")
  .replace(/[\u2010-\u2015]/g, "-")
  .replace(/\u00d7/g, "x")
  .replace(/\u2192/g, "to")
  .replace(/\u2265/g, ">=")
  .replace(/\u2264/g, "<=")
  .replace(/\u03a3/g, "SUM")
  .replace(/\u03bc/g, "AVG")
  .replace(/\u20b9/g, "INR")
  .replace(/[\u2080-\u2089]/g, (char) => "0123456789"[char.charCodeAt(0) - 0x2080])
  .replace(/[^\x20-\x7E]/g, "");

const rgb = (hex) => {
  const normalized = String(hex || COLORS.ink).replace("#", "");
  const value = normalized.length === 3
    ? normalized.split("").map((part) => part + part).join("")
    : normalized.slice(0, 6);
  return [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
};

const setColor = (doc, method, value) => doc[method](...rgb(value));
const fill = (doc, value) => setColor(doc, "setFillColor", value);
const stroke = (doc, value) => setColor(doc, "setDrawColor", value);
const ink = (doc, value) => setColor(doc, "setTextColor", value);

const rect = (doc, x, y, w, h, options = {}) => {
  const { fillColor, strokeColor = null, radius = 0, lineWidth = 0.25 } = options;
  if (fillColor) fill(doc, fillColor);
  if (strokeColor) {
    stroke(doc, strokeColor);
    doc.setLineWidth(lineWidth);
  }
  const style = fillColor && strokeColor ? "FD" : fillColor ? "F" : "S";
  if (radius) doc.roundedRect(x, y, w, h, radius, radius, style);
  else doc.rect(x, y, w, h, style);
};

const textLines = (doc, value, width, maxLines) => {
  const source = ascii(value);
  const lines = width ? doc.splitTextToSize(source, Math.max(1, width)) : [source];
  if (!maxLines || lines.length <= maxLines) return lines;
  const limited = lines.slice(0, maxLines);
  let final = limited[maxLines - 1];
  while (doc.getTextWidth(`${final}...`) > width && final.length) final = final.slice(0, -1);
  limited[maxLines - 1] = `${final.trim()}...`;
  return limited;
};

const drawText = (doc, value, x, y, options = {}) => {
  const {
    size = 8, color = COLORS.ink, weight = "normal", align = "left",
    width = null, maxLines = null, lineHeight = 1.2,
  } = options;
  doc.setFont("helvetica", weight === "bold" ? "bold" : weight === "italic" ? "italic" : "normal");
  doc.setFontSize(size);
  ink(doc, color);
  const lines = textLines(doc, value, width, maxLines);
  doc.text(lines, x, y, { align, lineHeightFactor: lineHeight });
  return lines.length * size * 0.3528 * lineHeight;
};

const drawTextBox = (doc, value, x, y, w, h, options = {}) => {
  const {
    size = 7, color = COLORS.ink, weight = "normal", align = "left",
    valign = "middle", padding = 2, maxLines = null, lineHeight = 1.2,
  } = options;
  doc.setFont("helvetica", weight === "bold" ? "bold" : weight === "italic" ? "italic" : "normal");
  doc.setFontSize(size);
  ink(doc, color);
  const usable = Math.max(1, w - padding * 2);
  const lines = textLines(doc, value, usable, maxLines);
  const lineMm = size * 0.3528 * lineHeight;
  const total = lines.length * lineMm;
  const baselineOffset = size * 0.3528 * 0.78;
  let start = y + padding + baselineOffset;
  if (valign === "middle") start = y + (h - total) / 2 + baselineOffset;
  if (valign === "bottom") start = y + h - padding - total + baselineOffset;
  const tx = align === "center" ? x + w / 2 : align === "right" ? x + w - padding : x + padding;
  doc.text(lines, tx, start, { align, lineHeightFactor: lineHeight });
};

const drawPill = (doc, label, x, y, w, h, color, tone, options = {}) => {
  rect(doc, x, y, w, h, { fillColor: tone, strokeColor: options.border || tone, radius: h / 2 });
  drawTextBox(doc, label, x, y, w, h, {
    size: options.size || 5.8, color, weight: "bold", align: "center", padding: 1, maxLines: 1,
  });
};

const drawProgress = (doc, x, y, w, h, value, color, inverse = false) => {
  const ratio = Math.max(0, Math.min(1, Number(value || 0) / 10));
  rect(doc, x, y, w, h, { fillColor: inverse ? "#789087" : "#E8EEEC", radius: h / 2 });
  if (ratio > 0) rect(doc, x, y, Math.max(h, w * ratio), h, {
    fillColor: inverse ? COLORS.white : color, radius: h / 2,
  });
};

const pageFooter = (doc, pageNumber, title) => {
  fill(doc, COLORS.teal);
  doc.rect(PAGE.left, 287.8, 18, 0.7, "F");
  drawText(doc, `TOSS ADVANCED ASSESSMENT | ${title}`, 35, 289.2, { size: 6.4, color: COLORS.muted });
  drawText(doc, `${pageNumber + 1} / 11`, PAGE.right, 289.2, { size: 6.4, color: COLORS.muted, align: "right" });
};

const sectionHeading = (doc, number, title, subtitle) => {
  drawText(doc, String(number).padStart(2, "0"), PAGE.left, 22, { size: 22, color: COLORS.aqua, weight: "bold" });
  drawText(doc, title, 31, 20.2, { size: 14, weight: "bold" });
  if (subtitle) drawText(doc, subtitle, 31, 25.8, { size: 7.2, color: COLORS.muted });
};

const appendixHeading = (doc, letter, title, subtitle) => {
  drawText(doc, `${letter}. ${title}`, PAGE.left, 20, { size: 10.5, weight: "bold" });
  stroke(doc, COLORS.aqua);
  doc.setLineWidth(0.45);
  doc.line(PAGE.left, 22.3, PAGE.right, 22.3);
  if (subtitle) drawText(doc, subtitle, PAGE.left, 27.2, { size: 6.2, color: COLORS.muted, width: 182, maxLines: 2 });
};

const cellStyleValue = (value, row, index) => typeof value === "function" ? value(row, index) : value;

const drawTable = (doc, config) => {
  const {
    x, y, width, columns, rows, headerHeight = 9, rowHeight = 8,
    headerFill = COLORS.ink, headerColor = COLORS.white, alternate = true,
    totalRows = new Set(), cellStyle = () => ({}), headerSize = 5.2, bodySize = 5.7,
  } = config;
  const widths = columns.map((column) => column.width * width);
  const rowHeights = rows.map((row, index) => cellStyleValue(rowHeight, row, index));
  let cursorX = x;
  columns.forEach((column, columnIndex) => {
    rect(doc, cursorX, y, widths[columnIndex], headerHeight, { fillColor: headerFill });
    drawTextBox(doc, column.label, cursorX, y, widths[columnIndex], headerHeight, {
      size: headerSize, color: headerColor, weight: "bold", align: column.align || "left", padding: 1.4, maxLines: 2,
    });
    cursorX += widths[columnIndex];
  });
  let cursorY = y + headerHeight;
  rows.forEach((row, rowIndex) => {
    const h = rowHeights[rowIndex];
    const isTotal = totalRows.has(rowIndex);
    const rowFill = isTotal ? "#48665C" : alternate && rowIndex % 2 ? COLORS.soft : COLORS.white;
    rect(doc, x, cursorY, width, h, { fillColor: rowFill });
    cursorX = x;
    columns.forEach((column, columnIndex) => {
      const style = cellStyle(row, column, rowIndex, columnIndex) || {};
      if (style.fillColor) rect(doc, cursorX, cursorY, widths[columnIndex], h, { fillColor: style.fillColor });
      stroke(doc, style.strokeColor || COLORS.line);
      doc.setLineWidth(0.2);
      doc.rect(cursorX, cursorY, widths[columnIndex], h, "S");
      const rawValue = typeof column.value === "function" ? column.value(row, rowIndex) : row[column.key];
      drawTextBox(doc, rawValue, cursorX, cursorY, widths[columnIndex], h, {
        size: style.size || bodySize,
        color: style.color || (isTotal ? COLORS.white : COLORS.ink),
        weight: style.weight || (isTotal ? "bold" : "normal"),
        align: style.align || column.align || "left",
        padding: style.padding ?? 1.4,
        maxLines: style.maxLines || column.maxLines || 2,
        lineHeight: style.lineHeight || 1.1,
      });
      cursorX += widths[columnIndex];
    });
    cursorY += h;
  });
  return cursorY;
};

const scoreColor = (score) => score >= 8 ? COLORS.greenDark : score >= 6 ? "#388E3C" : score >= 4 ? COLORS.orange : score >= 2 ? "#EF6C00" : COLORS.red;
const scoreBand = (score) => score >= 7
  ? { label: "Good", color: COLORS.green, tone: COLORS.greenSoft }
  : score >= 4
    ? { label: "Moderate", color: "#EF6C00", tone: COLORS.orangeSoft }
    : { label: "Critical", color: "#C62828", tone: "#FFEBEE" };
const methodologyBand = (score) => score >= 8.5
  ? { label: "Excellent", color: COLORS.greenDark, tone: COLORS.greenSoft }
  : score >= 7
    ? { label: "Good", color: "#388E3C", tone: "#EDF7ED" }
    : score >= 5
      ? { label: "Average", color: "#9A6700", tone: "#FFF8E1" }
      : score >= 3
        ? { label: "Below average", color: "#EF6C00", tone: COLORS.orangeSoft }
        : { label: "Poor", color: "#C62828", tone: "#FFEBEE" };
const transparencyBand = (score) => score >= 8
  ? { label: "Declared verified", color: COLORS.greenDark, tone: COLORS.greenSoft }
  : score >= 4
    ? { label: "Self-reported", color: "#EF6C00", tone: COLORS.orangeSoft }
    : { label: "Estimated", color: "#C62828", tone: "#FFEBEE" };
const confidenceBand = (score) => score >= 8
  ? { label: "High", detail: "High declared evidence confidence", color: COLORS.greenDark }
  : score >= 4
    ? { label: "Moderate", detail: "Moderate declared evidence confidence", color: COLORS.orange }
    : { label: "Low", detail: "Low declared evidence confidence", color: COLORS.red };
const ratingBand = (score) => score >= 8
  ? { label: "Excellent", color: COLORS.greenDark }
  : score >= 6
    ? { label: "Good", color: "#388E3C" }
    : score >= 4
      ? { label: "Moderate", color: COLORS.orange }
      : { label: "Poor", color: COLORS.red };

const formatValue = (value) => {
  if (value == null) return "Unavailable";
  const number = Number(value || 0);
  if (Math.abs(number) >= 1000) return number.toLocaleString("en-US", { maximumFractionDigits: 1 });
  if (Math.abs(number) >= 100) return number.toFixed(1);
  return number.toFixed(2);
};

const average = (values) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

const inferActionStage = (title = "") => {
  const lowered = title.toLowerCase();
  const match = STAGES_ORDER.find((stage) => lowered.includes(STAGE_FULL[stage].toLowerCase()));
  if (match) return STAGE_FULL[match];
  if (lowered.includes("supply chain")) return "Supply Chain";
  if (lowered.includes("transparency")) return "All lifecycle stages";
  if (lowered.includes("material") || lowered.startsWith("switch ")) return "Raw Material";
  return "Cross-lifecycle";
};

const buildModel = (data, countryData = {}) => {
  const results = data.results || {};
  const paramsByStage = buildParamsByStage(results, "advanced");
  const activeSubstages = SUBSTAGES_ORDER.filter((stage) => results.parameter_scores?.[stage]);
  const transparencyValues = activeSubstages.flatMap((stage) => PARAM_KEYS
    .map((parameter) => Number(results.parameter_scores?.[stage]?.[parameter]?.transparency))
    .filter(Number.isFinite));
  const avgTransparency = average(transparencyValues);
  const substageTransparency = Object.fromEntries(activeSubstages.map((stage) => [
    stage,
    average(PARAM_KEYS.map((parameter) => Number(results.parameter_scores?.[stage]?.[parameter]?.transparency)).filter(Number.isFinite)),
  ]));
  const bom = results.mass_fractions || data.bom_items || [];
  const productMass = Number(results.unit_summary?.audit?.effective_product_mass_kg)
    || bom.reduce((sum, item) => sum + (Number(item.quantity) || 1) * (Number(item.weight_kg) || 0), 0);
  const fallback = aggregateStageInputs(data.stage_inputs || {}, buildCountryInferredRows(bom, countryData));
  const aggregates = Object.fromEntries(PARAM_KEYS.map((parameter) => {
    const backend = results.unit_summary?.parameter_aggregates?.[parameter];
    return [parameter, backend ? {
      value: backend.display_value, unit: backend.display_unit,
      method: backend.method, count: backend.count,
    } : fallback[parameter]];
  }));
  const actions = top5Actions({ results, paramsByStage, mode: "advanced", bom });
  const ladder = criticalityLadder(results, "advanced");
  const confidence = confidenceBand(avgTransparency);
  const actionConfidence = (action) => {
    const stage = STAGES_ORDER.find((key) => action.title.toLowerCase().includes(STAGE_FULL[key].toLowerCase()));
    if (!stage) return confidence.label;
    const values = Object.values(paramsByStage[stage] || {}).map((item) => Number(item.transparency || 0));
    return confidenceBand(average(values)).label;
  };
  const lifecycleDiagnostics = STAGES_ORDER.map((stageKey, stageIndex) => ({
    key: stageKey,
    index: stageIndex + 1,
    label: stageKey === "logistics" ? "Supply Chain" : STAGE_FULL[stageKey],
    substages: activeSubstages.filter((substage) => SS_STAGE_KEY[substage] === stageKey).map((substage) => {
      const score = Number(results.ssi?.[substage] || 0);
      const weakest = PARAM_KEYS.map((parameter) => ({
        key: parameter, score: Number(results.parameter_scores?.[substage]?.[parameter]?.adjusted),
      })).filter((item) => Number.isFinite(item.score)).sort((a, b) => a.score - b.score)[0];
      return {
        key: substage,
        label: SS_FULL[substage]?.split(" - ").slice(1).join(" - ") || SS_FULL[substage],
        score,
        band: methodologyBand(score),
        evidence: transparencyBand(substageTransparency[substage] || 0),
        weakest: weakest ? `${PL[weakest.key]} (${score100(weakest.score, 2)})` : "Not available",
      };
    }),
  })).filter((stage) => stage.substages.length);
  const parameterCards = PARAM_KEYS.map((parameter) => {
    const stageValues = STAGES_ORDER.filter((stage) => paramsByStage[stage]?.[parameter]?.adjusted != null).map((stage) => ({
      stage,
      label: STAGE_SHORT[stage],
      value: Number(paramsByStage[stage]?.[parameter]?.adjusted || 0),
    }));
    const value = stageValues.length ? average(stageValues.map((row) => row.value)) : null;
    return { key: parameter, label: PL_SHORT[parameter], value, band: value == null ? {label:'N/A',color:COLORS.muted,tone:COLORS.soft} : scoreBand(value), stageValues };
  });
  const causes = rootCauses(results.parameter_scores || {}, results.ssi || {}, 5).map((cause) => ({
    ...cause,
    label: SS_FULL[cause.key] || cause.key,
  }));
  return {
    data, results, paramsByStage, activeSubstages, substageTransparency, avgTransparency,
    confidence, bom, productMass, aggregates, actions, ladder, actionConfidence,
    lifecycleDiagnostics, parameterCards, causes,
    buckets: productBuckets(results),
    dominance: paramDominance(paramsByStage),
    pollution: primaryPollutionDriver(paramsByStage),
    variability: variabilityByStage(paramsByStage),
    rating: ratingBand(Number(results.upsi || 0)),
  };
};

const drawSemicircleGauge = (doc, cx, cy, radius, value, color) => {
  const segments = 28;
  const paintArc = (from, to, arcColor, lineWidth) => {
    stroke(doc, arcColor);
    doc.setLineWidth(lineWidth);
    doc.setLineCap("butt");
    let previous = null;
    for (let index = from; index <= to; index += 1) {
      const angle = Math.PI + (Math.PI * index) / segments;
      const point = [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
      if (previous) doc.line(previous[0], previous[1], point[0], point[1]);
      previous = point;
    }
  };
  paintArc(0, segments, "#E9EEEC", 3.4);
  paintArc(0, Math.max(1, Math.round((Math.max(0, Math.min(10, value)) / 10) * segments)), color, 3.4);
};

// A standalone A4 cover, drawn as vectors so text stays selectable and sharp.
const reportCover = (doc, model) => {
  const green = "#075138";
  const muted = "#526479";
  const shape = (color, commands) => {
    fill(doc, color);
    doc.path(commands);
    doc.fill();
  };
  // Layered leaf curves mirror the reference's light, open lower-right composition.
  doc.saveGraphicsState();
  doc.rect(0, 0, PAGE.w, PAGE.h, null); doc.clip(); doc.discardPath();
  shape("#EDF5F0", [
    {op:"m",c:[210,116]}, {op:"c",c:[160,129,134,179,156,216]},
    {op:"c",c:[175,247,201,243,218,219]}, {op:"l",c:[218,116]}, {op:"h",c:[]},
  ]);
  shape("#EAF3ED", [
    {op:"m",c:[69,297]}, {op:"c",c:[108,244,149,218,201,235]},
    {op:"c",c:[224,245,229,273,216,297]}, {op:"h",c:[]},
  ]);
  shape("#CFE6D8", [
    {op:"m",c:[113,297]}, {op:"c",c:[146,249,177,222,211,212]},
    {op:"l",c:[211,297]}, {op:"h",c:[]},
  ]);
  shape("#2E7553", [
    {op:"m",c:[137,297]}, {op:"c",c:[169,277,152,229,211,192]},
    {op:"l",c:[211,297]}, {op:"h",c:[]},
  ]);
  shape("#18543B", [
    {op:"m",c:[137,297]}, {op:"c",c:[166,278,158,249,178,228]},
    {op:"c",c:[192,230,201,234,211,239]}, {op:"l",c:[211,297]}, {op:"h",c:[]},
  ]);
  stroke(doc, "#FFFFFF"); doc.setLineWidth(0.55);
  doc.path([{op:"m",c:[155,270]},{op:"c",c:[142,229,164,196,214,173]}]);doc.stroke();
  doc.restoreGraphicsState();

  // Leaf mark plus wordmark, kept local and independent of external image services.
  shape(green, [{op:"m",c:[15,26]},{op:"c",c:[13,20,17,17,23,16]},
    {op:"c",c:[23,24,21,27,16,26]},{op:"h",c:[]}]);
  stroke(doc, COLORS.white);doc.setLineWidth(0.35);
  doc.path([{op:"m",c:[15,27]},{op:"c",c:[16,23,18,21,21,19]}]);doc.stroke();
  drawText(doc, "TOSS", 27, 25, {size:26,weight:"bold",color:green});
  drawText(doc, "Traceable Object Sustainability Score", 15, 32, {size:6.2,color:muted});
  stroke(doc, "#AAB5BB");doc.setLineWidth(0.3);doc.line(65,16,65,28);
  drawText(doc, "PRODUCT", 70, 19, {size:8,weight:"bold",color:muted});
  drawText(doc, "SUSTAINABILITY", 70, 23, {size:8,weight:"bold",color:muted});
  drawText(doc, "INDEX", 70, 27, {size:8,weight:"bold",color:muted});
  drawText(doc, "Product Sustainability,", 143, 21, {size:9.5,weight:"bold",color:green});
  drawText(doc, "Simplified into One Score.", 143, 26, {size:9.5,weight:"bold",color:green});
  rect(doc,143,31,12.5,0.8,{fillColor:"#288650",radius:0.4});

  rect(doc,15,73.5,14,0.8,{fillColor:"#288650",radius:0.4});
  const coverProductName = model.data.product_name || "Product assessment";
  let coverTitleSize = 48;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(coverTitleSize);
  while (doc.splitTextToSize(ascii(coverProductName), 180).length > 2 && coverTitleSize > 24) {
    doc.setFontSize(--coverTitleSize);
  }
  const coverTitleHeight = drawText(doc, coverProductName, 15, 96, {
    size:coverTitleSize,weight:"bold",color:green,width:180,maxLines:2,lineHeight:1.05,
  });
  const subtitleY = Math.max(114, 96 + coverTitleHeight + 2);
  drawText(doc, "Toss Index & Assessment Report", 15, subtitleY, {size:19,color:"#424F5E"});
  drawText(doc, "PRODUCT   /   PLANET   /   RESPONSIBLE BUSINESS", 15, subtitleY + 20, {size:9.2,color:muted});

  stroke(doc,"#D1DADC");doc.setLineWidth(0.3);doc.line(15,160,90,160);
  drawText(doc, "ASSESSMENT ID", 15, 170, {size:7.2,weight:"bold",color:muted});
  drawText(doc, model.data.id || "Not specified", 15, 176, {size:10.6,color:"#424F5E",width:123,maxLines:2});
  drawText(doc, "REPORT DATE", 15, 188, {size:7.2,weight:"bold",color:muted});
  const date = model.data.created_at ? new Date(model.data.created_at) : null;
  drawText(doc, date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("en-GB") : "Not specified", 15, 194, {size:10.6,color:"#424F5E"});

  drawText(doc, "@2026 Toss Index",15,267.5,{size:8,weight:"bold",color:muted});
  drawText(doc, "From Hypes.in",15,272,{size:8,weight:"bold",color:muted});
  rect(doc,15,277,11,0.7,{fillColor:"#288650",radius:0.35});
};

const pageOne = (doc, model) => {
  const { data, results, productMass, rating, confidence, buckets, aggregates, actions } = model;
  const score = Number(results.upsi || 0);
  rect(doc, 12, 12, 186, 15, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "TOSS", 18, 22, { size: 15, weight: "bold", color: COLORS.teal });
  stroke(doc, COLORS.line); doc.line(42, 15, 42, 24); doc.line(140, 15, 140, 24);
  drawText(doc, "PRODUCT SUSTAINABILITY INDEX", 47, 21, { size: 6.2, color: COLORS.muted, weight: "bold" });
  drawText(doc, "ASSESSMENT ID", 145, 17.5, { size: 4.8, color: COLORS.muted, weight: "bold" });
  drawText(doc, data.id || "-", 145, 22, { size: 4.8, color: COLORS.muted, width: 49, maxLines: 1 });

  doc.saveGraphicsState();
  doc.roundedRect(12, 30, 186, 74, 2, 2, null); doc.clip(); doc.discardPath();
  doc.addImage(REPORT_FOREST_IMAGE, "JPEG", 12, 30, 186, 74);
  doc.restoreGraphicsState();
  drawText(doc, "ASSESSMENT REPORT", 18, 42, { size: 6.8, color: COLORS.white, weight: "bold" });
  drawPill(doc, "ADVANCED", 67, 36.5, 25, 8, COLORS.white, "#245D43", { size: 6, border: "#6CA686" });
  // Keep two-line product names clear of the subtitle and metadata.
  const titleHeight = drawText(doc, data.product_name || "Product assessment", 18, 57, { size: 21, color: COLORS.white, weight: "bold", width: 112, maxLines: 2, lineHeight: 1.05 });
  drawText(doc, data.product_type || "Product assessment", 18, Math.max(67, 57 + titleHeight), { size: 7.5, color: "#E0E9E3", width: 112, maxLines: 1 });
  stroke(doc, "#5D8D76"); doc.line(18, 78, 29, 78);
  const meta = [
    ["REPORTING UNIT", data.functional_unit || "Not specified"],
    ["PRODUCT MASS", `${formatValue(productMass)} kg`],
    ["REPORT DATE", data.created_at ? new Date(data.created_at).toLocaleDateString("en-GB") : "-"],
  ];
  meta.forEach(([label, value], index) => {
    const x = 18 + index * 39;
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.7 }));
    rect(doc, x, 84, 36, 15, { fillColor: "#214E3D", strokeColor: "#54866D", radius: 1 });
    doc.restoreGraphicsState();
    stroke(doc, "#E2EFE7"); doc.setLineWidth(0.3);
    const ix = x + 2.2, iy = 90;
    if (index === 0) {
      doc.lines([[2,-1.2],[2,1.2],[0,3],[-2,1.2],[-2,-1.2],[0,-3]],ix,iy,[1,1],'S',true);
      doc.line(ix,iy,ix+2,iy+1.2);doc.line(ix+2,iy+1.2,ix+4,iy);doc.line(ix+2,iy+1.2,ix+2,iy+4.2);
    } else if (index === 1) {
      doc.roundedRect(ix,iy,4,4,0.4,0.4,'S');doc.circle(ix+2,iy,0.8,'S');
    } else {
      doc.roundedRect(ix,iy,4,4,0.4,0.4,'S');doc.line(ix,iy+1,ix+4,iy+1);doc.line(ix+1,iy-0.7,ix+1,iy+0.5);doc.line(ix+3,iy-0.7,ix+3,iy+0.5);
    }
    drawText(doc, label, x + 8, 89.5, { size: 4, color: "#E0E9E3", weight: "bold" });
    drawText(doc, value, x + 8, 95, { size: 6.8, color: COLORS.white, weight: "bold", width: 26, maxLines: 1 });
  });
  rect(doc, 139, 35, 54, 64, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "OVERALL TOSS INDEX", 144, 43, { size: 6.1, color: COLORS.teal, weight: "bold" });
  drawText(doc, score100(score, 2), 144, 60, { size: 32, color: COLORS.teal, weight: "bold" });
  drawText(doc, "out of 100", 144, 67, { size: 7, color: COLORS.muted });
  drawProgress(doc, 144, 71, 44, 1.2, score, COLORS.teal);
  drawPill(doc, rating.label, 144, 76, 29, 8, rating.color, rating.tone || "#FFF0F0", { size: 10 });
  drawText(doc, "Product Sustainability Index", 144, 88, { size: 5.4, color: COLORS.muted });
  rect(doc, 140, 91, 52, 7, { fillColor: "#EDF5F0", radius: 1 });
  drawText(doc, "A MORE SUSTAINABLE TOMORROW", 144, 95.5, { size: 4.6, color: COLORS.teal, weight: "bold" });

  drawText(doc, "ESGE PERFORMANCE", 16, 113, { size: 7.2, weight: "bold" });
  const dimensions = [
    ["Environmental", buckets.environmental, ESGE_COLORS.environmental],
    ["Social", buckets.social, ESGE_COLORS.social],
    ["Governance", buckets.governance, ESGE_COLORS.governance],
    ["Economic", buckets.economic, ESGE_COLORS.economic],
  ];
  dimensions.forEach(([label, value, color], index) => {
    const x = 16 + index * 31;
    rect(doc, x, 117, 29, 28, { fillColor: COLORS.soft, strokeColor: COLORS.line, radius: 2 });
    rect(doc, x, 117, 29, 1.1, { fillColor: color, radius: 1 });
    drawText(doc, label, x + 3, 124, { size: 5.7, color: COLORS.muted, weight: "bold" });
    drawText(doc, score100(Number(value || 0), 2), x + 3, 136, { size: 15, color, weight: "bold" });
    drawText(doc, "out of 100", x + 3, 141.5, { size: 4.6, color: COLORS.muted });
  });
  rect(doc, 142, 117, 52, 28, { fillColor: COLORS.soft, strokeColor: COLORS.line, radius: 2 });
  rect(doc, 142, 117, 1.2, 28, { fillColor: confidence.color, radius: 1 });
  drawText(doc, "DATA CONFIDENCE", 147, 124, { size: 5.6, color: COLORS.muted, weight: "bold" });
  drawText(doc, confidence.label, 147, 134.5, { size: 14, color: confidence.color, weight: "bold" });
  drawText(doc, `${score100(model.avgTransparency, 2)} / 100`, 147, 141.5, { size: 6.3, weight: "bold" });

  const headlines = [
    ["GWP", aggregates.gwp?.value, aggregates.gwp?.unit || PARAM_UNITS.gwp, COLORS.green],
    ["WATER", aggregates.water?.value, aggregates.water?.unit || PARAM_UNITS.water, COLORS.blue],
    ["CIRCULARITY", aggregates.circularity?.value, aggregates.circularity?.unit || PARAM_UNITS.circularity, COLORS.teal],
  ];
  headlines.forEach(([label, value, unit, color], index) => {
    const x = 16 + index * 60;
    rect(doc, x, 150, 57, 23, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
    rect(doc, x + 4, 155, 12, 12, { fillColor: `${color === COLORS.green ? "#EAF4EB" : color === COLORS.blue ? "#E9F2FD" : "#E6F4F1"}`, radius: 6 });
    drawTextBox(doc, label.slice(0, 1), x + 4, 155, 12, 12, { size: 8, color, weight: "bold", align: "center" });
    drawText(doc, label, x + 20, 157, { size: 5.2, color: COLORS.muted, weight: "bold" });
    drawText(doc, value == null && unit === "See stage indicators" ? "Separate" : formatValue(value), x + 20, 165, { size: 12, weight: "bold" });
    drawText(doc, unit || "-", x + 20, 170, { size: 4.3, color: COLORS.muted, width: 33, maxLines: 1 });
  });

  rect(doc, 16, 178, 178, 47, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "LIFECYCLE-STAGE STATUS", 20, 186, { size: 7.1, weight: "bold" });
  const stages = STAGES_ORDER.filter(stage => results.stage_si?.[stage] != null).map((stage) => ({ label: STAGE_FULL[stage], value: Number(results.stage_si?.[stage] || 0) }));
  stages.forEach((stage, index) => {
    const x = 23 + index * 34;
    const band = scoreBand(stage.value);
    if (index < stages.length - 1) {
      stroke(doc, COLORS.line);
      doc.setLineWidth(0.7);
      doc.line(x + 12, 195, x + 34, 195);
    }
    rect(doc, x, 190, 10, 10, { fillColor: band.color, strokeColor: COLORS.white, radius: 5, lineWidth: 1.2 });
    drawTextBox(doc, String(index + 1).padStart(2, "0"), x, 190, 10, 10, { size: 4.4, color: COLORS.white, weight: "bold", align: "center" });
    drawTextBox(doc, stage.label, x - 6, 202, 22, 8, { size: 5.2, color: COLORS.muted, weight: "bold", align: "center", maxLines: 2 });
    drawText(doc, score100(stage.value, 2), x + 5, 215, { size: 10.5, weight: "bold", align: "center" });
    drawPill(doc, band.label, x - 5, 217, 20, 5.5, band.color, band.tone, { size: 4.5 });
  });

  rect(doc, 16, 230, 178, 42, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "THREE HIGHEST-PRIORITY IMPROVEMENTS", 20, 238, { size: 7.1, weight: "bold" });
  actions.slice(0, 3).forEach((action, index) => {
    const x = 20 + index * 57;
    rect(doc, x, 243, 54, 24, { fillColor: COLORS.soft, radius: 2 });
    rect(doc, x + 2, 250, 8, 8, { fillColor: COLORS.teal, radius: 4 });
    drawTextBox(doc, index + 1, x + 2, 250, 8, 8, { size: 5.3, color: COLORS.white, weight: "bold", align: "center" });
    drawTextBox(doc, action.title, x + 12, 245, 29.5, 20, { size: 5.1, weight: "bold", maxLines: 3, padding: 0.8 });
    drawTextBox(doc, action.impact, x + 41.5, 243, 11.5, 24, { size: 7.2, color: COLORS.teal, weight: "bold", align: "right", padding: 0.5, maxLines: 1 });
  });
  pageFooter(doc, 1, "Summary label");
};

const pageTwo = (doc, model) => {
  sectionHeading(doc, 2, "Lifecycle Diagnostic Breakdown", "Official substage results, organized by the five lifecycle stages.");
  const placements = [
    [12, 37, 90], [108, 37, 90], [12, 92, 90], [108, 92, 90], [12, 165, 186],
  ];
  model.lifecycleDiagnostics.forEach((stage, stageIndex) => {
    const [x, y, width] = placements[stageIndex] || [12, 165, 186];
    const rowHeight = 18;
    const height = 13 + stage.substages.length * rowHeight;
    rect(doc, x, y, width, height, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
    rect(doc, x, y, width, 13, { fillColor: COLORS.ink, radius: 2 });
    rect(doc, x + 4, y + 2.5, 8, 8, { fillColor: COLORS.aqua, radius: 4 });
    drawTextBox(doc, String(stage.index).padStart(2, "0"), x + 4, y + 2.5, 8, 8, { size: 4.5, color: COLORS.ink, weight: "bold", align: "center" });
    drawTextBox(doc, stage.label, x + 15, y, width - 33, 13, { size: 7.1, color: COLORS.white, weight: "bold" });
    drawTextBox(doc, `${stage.substages.length} substages`, x + width - 23, y, 20, 13, { size: 4.8, color: "#9BDED5", align: "right" });
    stage.substages.forEach((substage, index) => {
      const rowY = y + 13 + index * rowHeight;
      if (index % 2) rect(doc, x, rowY, width, rowHeight, { fillColor: COLORS.soft });
      if (index) {
        stroke(doc, COLORS.line);
        doc.setLineWidth(0.2);
        doc.line(x, rowY, x + width, rowY);
      }
      const labelW = width * 0.38;
      const scoreW = width * 0.17;
      const bandW = width * 0.21;
      const evidenceW = width - labelW - scoreW - bandW;
      const scoreX = x + labelW;
      const bandX = scoreX + scoreW;
      const evidenceX = bandX + bandW;
      drawTextBox(doc, substage.label, x + 2, rowY + 0.5, labelW - 3, 7, { size: 5.8, weight: "bold", maxLines: 1, padding: 1 });
      drawTextBox(doc, `Weakest: ${substage.weakest}`, x + 2, rowY + 7, labelW - 3, 10, { size: 4.3, color: COLORS.muted, maxLines: 2, padding: 1 });
      drawTextBox(doc, score100(substage.score, 2), scoreX, rowY + 1, scoreW, 10, { size: 7.3, weight: "bold", align: "center", maxLines: 1, padding: 0.5 });
      drawTextBox(doc, "Parameter Avg. / 100", scoreX, rowY + 10.2, scoreW, 6, { size: 3.9, color: COLORS.muted, weight: "bold", align: "center", maxLines: 2, padding: 0.4 });
      drawPill(doc, substage.band.label, bandX + 1, rowY + 5.5, bandW - 2, 7, substage.band.color, substage.band.tone, { size: 4.4 });
      drawTextBox(doc, substage.evidence.label, evidenceX + 0.5, rowY + 1, evidenceW - 1, 16, { size: 4.3, color: substage.evidence.color, weight: "bold", align: "center", maxLines: 2, padding: 0.8 });
    });
  });
  rect(doc, 12, 236, 186, 23, { fillColor: COLORS.mint, strokeColor: COLORS.line, radius: 2 });
  rect(doc, 16, 242, 9, 9, { fillColor: COLORS.teal, radius: 4.5 });
  drawTextBox(doc, "i", 16, 242, 9, 9, { size: 6.5, color: COLORS.white, weight: "bold", align: "center" });
  drawTextBox(doc, "No new group averages are introduced here. Parameter Avg., transparency and weakest-parameter values come directly from the existing normalized and transparency-adjusted calculation outputs.", 29, 238, 164, 19, { size: 6.4, color: COLORS.muted, maxLines: 3 });
  pageFooter(doc, 2, "Lifecycle diagnostic breakdown");
};

const pageThree = (doc, model) => {
  sectionHeading(doc, 3, "Improvement Plan", "Priorities based on current performance; score changes require complete recalculation.");
  const rows = model.actions.map((action) => {
    const impact = action.impact;
    return {
      action: `${action.priority}. ${action.title}. Basis: ${action.rationale}`,
      gain: "Recalculate",
      stage: inferActionStage(action.title),
      impact,
      confidence: model.actionConfidence(action),
    };
  });
  drawTable(doc, {
    x: 12, y: 37, width: 186, headerHeight: 10, rowHeight: 20,
    columns: [
      { key: "action", label: "Priority action", width: 0.45 },
      { key: "gain", label: "Score change", width: 0.12, align: "center" },
      { key: "stage", label: "Lifecycle stage", width: 0.18, align: "center" },
      { key: "impact", label: "Priority level", width: 0.12, align: "center" },
      { key: "confidence", label: "Confidence", width: 0.13, align: "center" },
    ],
    rows,
    bodySize: 6.1,
    cellStyle: (row, column) => column.key === "gain"
      ? { color: COLORS.teal, weight: "bold", size: 8 }
      : column.key === "impact"
        ? { color: row.impact === "High" ? COLORS.red : row.impact === "Medium" ? COLORS.orange : COLORS.green, weight: "bold" }
        : column.key === "action" ? { weight: "bold", maxLines: 4 } : {},
  });

  rect(doc, 12, 157, 186, 105, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "CRITICALITY PROFILE", 17, 167, { size: 7.2, weight: "bold" });
  drawText(doc, "Higher values indicate greater intervention priority", 193, 167, { size: 5.4, color: COLORS.muted, align: "right" });
  const chart = { x: 24, y: 178, w: 163, h: 61 };
  [0, 2.5, 5, 7.5, 10].forEach((tick) => {
    const yy = chart.y + chart.h - (tick / 10) * chart.h;
    stroke(doc, COLORS.line);
    doc.setLineWidth(0.2);
    doc.line(chart.x, yy, chart.x + chart.w, yy);
    drawText(doc, tick * 10, chart.x - 3, yy + 1, { size: 4.7, color: COLORS.muted, align: "right" });
  });
  const ladder = model.ladder;
  const points = ladder.map((row, index) => ({
    x: chart.x + (index * chart.w) / Math.max(1, ladder.length - 1),
    y: chart.y + chart.h - (Math.max(0, Math.min(10, row.criticality)) / 10) * chart.h,
    row,
  }));
  stroke(doc, COLORS.red);
  doc.setLineWidth(0.9);
  for (let index = 1; index < points.length; index += 1) doc.line(points[index - 1].x, points[index - 1].y, points[index].x, points[index].y);
  points.forEach(({ x, y, row }) => {
    fill(doc, COLORS.white);
    stroke(doc, COLORS.red);
    doc.setLineWidth(0.9);
    doc.circle(x, y, 1.6, "FD");
    drawText(doc, score100(Number(row.criticality || 0), 2), x, y - 3.4, { size: 4.7, weight: "bold", align: "center" });
    drawTextBox(doc, SS_SHORT[row.key] || row.name, x - 8, 242, 16, 12, { size: 4.3, color: COLORS.muted, align: "center", maxLines: 2, valign: "top" });
  });
  pageFooter(doc, 3, "Improvement plan");
};

const pageFour = (doc, model) => {
  sectionHeading(doc, 4, "Verification and Methodology", "A concise record of scope, units, evidence quality and validation.");
  const audit = model.results.unit_summary?.audit;
  const cards = [
    ["REPORTING UNIT", model.data.functional_unit || "Not specified", "Applied to product-level impact output."],
    ["PRODUCT MASS AND BOM", `${formatValue(model.productMass)} kg`, `${model.bom.length} BOM lines; ${model.results.process_validation_version ? "quantity x unit mass reconciled." : "current mass validation not recorded."}`],
    ["SYSTEM BOUNDARY", "Assessed lifecycle coverage", `${Object.keys(model.results.stage_si || {}).length} lifecycle stages and ${model.activeSubstages.length} assessed substages.`],
    ["BENCHMARK SOURCE", "Country / world / product", "Selected country datasets and product benchmarks."],
    ["TRANSPARENCY CLASSIFICATION", model.confidence.detail, `Average evidence score ${score100(model.avgTransparency, 2)} / 100.`],
    ["CALCULATION VERSION", model.results.methodology_version || "TOSS Advanced V1", model.results.unit_basis_version ? "Explicit source units and reference masses." : "Scoring methodology unchanged."],
  ];
  cards.forEach(([label, value, note], index) => {
    const col = index % 3;
    const row = Math.floor(index / 3);
    const x = 12 + col * 63;
    const y = 37 + row * 48;
    rect(doc, x, y, 59, 43, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
    drawText(doc, label, x + 4, y + 8, { size: 5, color: COLORS.muted, weight: "bold", width: 51, maxLines: 1 });
    drawText(doc, value, x + 4, y + 19, { size: 8.3, color: label.includes("TRANSPARENCY") ? model.confidence.color : COLORS.ink, weight: "bold", width: 51, maxLines: 2 });
    drawText(doc, note, x + 4, y + 31, { size: 5.6, color: COLORS.muted, width: 51, maxLines: 3 });
  });
  rect(doc, 12, 137, 121, 92, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "UNITS USED", 17, 147, { size: 7, weight: "bold" });
  drawText(doc, "Canonical scoring contract", 128, 147, { size: 5.2, color: COLORS.muted, align: "right" });
  PARAM_KEYS.forEach((parameter, index) => {
    const col = index % 3;
    const row = Math.floor(index / 3);
    const x = 17 + col * 37.5;
    const y = 153 + row * 17;
    rect(doc, x, y, 34.5, 14, { fillColor: COLORS.soft, strokeColor: COLORS.line, radius: 1.5 });
    drawText(doc, PL[parameter], x + 2.5, y + 5.4, { size: 4.9, weight: "bold", width: 29, maxLines: 1 });
    drawText(doc, advancedUnit(model.results.unit_summary?.audit?.canonical_units?.[parameter] || PARAM_UNITS[parameter]), x + 2.5, y + 10.8, { size: 4.2, color: COLORS.muted, width: 29, maxLines: 1 });
  });
  rect(doc, 137, 137, 61, 92, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "UNIT-VALIDATION FINDINGS", 142, 147, { size: 6.8, weight: "bold" });
  const issues = audit?.issues || [];
  drawText(doc, `${issues.length} ${issues.length === 1 ? "finding" : "findings"}`, 142, 165, { size: 18, color: issues.some((item) => item.severity === "error") ? COLORS.red : COLORS.green, weight: "bold" });
  const findingText = issues.length
    ? `${issues.filter((item) => item.severity === "error").length} blocking and ${issues.filter((item) => item.severity !== "error").length} advisory findings. See the Technical Appendix for each conversion and responsible function.`
    : (!model.results.process_validation_version ? "Legacy result: current validation was not recorded." : "Saved automated checks recorded no unit errors; this is not independent verification.");
  drawText(doc, findingText, 142, 178, { size: 6.2, color: COLORS.muted, width: 51, maxLines: 7, lineHeight: 1.35 });
  rect(doc, 12, 235, 186, 27, { fillColor: COLORS.ink, radius: 2 });
  drawText(doc, "ASSUMPTIONS AND INFERRED VALUES", 17, 244, { size: 4.8, color: "#8EDBD0", weight: "bold" });
  drawText(doc, `${(audit?.conversions?.length || 0) + (audit?.basis_conversions?.length || 0)} documented conversions`, 17, 252, { size: 7, color: COLORS.white, weight: "bold" });
  drawText(doc, "ASSESSMENT ID", 78, 244, { size: 4.8, color: "#8EDBD0", weight: "bold" });
  drawText(doc, model.data.id || "-", 78, 252, { size: 6.1, color: COLORS.white, weight: "bold", width: 72, maxLines: 1 });
  drawPill(doc, "REFERENCE", 166, 243, 24, 10, COLORS.ink, COLORS.aqua, { size: 5.2 });
  pageFooter(doc, 4, "Verification and methodology");
};

const drawRadar = (doc, model, x, y, w, h) => {
  const cx = x + w / 2;
  const cy = y + h / 2 + 2;
  const radius = Math.min(w, h) * 0.34;
  const stages = STAGES_ORDER;
  const point = (index, value) => {
    const angle = -Math.PI / 2 + (Math.PI * 2 * index) / stages.length;
    const r = radius * (value / 10);
    return [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r];
  };
  [2, 4, 6, 8, 10].forEach((value) => {
    const points = stages.map((_, index) => point(index, value));
    stroke(doc, COLORS.line);
    doc.setLineWidth(0.2);
    points.forEach((p, index) => doc.line(p[0], p[1], points[(index + 1) % points.length][0], points[(index + 1) % points.length][1]));
  });
  stages.forEach((stage, index) => {
    const outer = point(index, 10);
    stroke(doc, COLORS.line);
    doc.line(cx, cy, outer[0], outer[1]);
    const label = point(index, 12.4);
    drawTextBox(doc, STAGE_SHORT[stage], label[0] - 8, label[1] - 3, 16, 6, { size: 4.2, color: COLORS.muted, align: "center" });
  });
  const series = [
    [stages.map((stage, index) => point(index, Number(model.results.stage_si?.[stage] || 0))), COLORS.green],
    [stages.map((stage, index) => point(index, Number(model.results.stage_esge?.[stage]?.esge || 0))), COLORS.blue],
  ];
  series.forEach(([points, color]) => {
    stroke(doc, color);
    doc.setLineWidth(0.65);
    points.forEach((p, index) => doc.line(p[0], p[1], points[(index + 1) % points.length][0], points[(index + 1) % points.length][1]));
    points.forEach((p) => { fill(doc, color); doc.circle(p[0], p[1], 0.75, "F"); });
  });
};

const drawStackedBars = (doc, model, x, y, w, h) => {
  const baseY = y + h - 10;
  const maxH = h - 18;
  const barW = 8;
  STAGES_ORDER.forEach((stage, index) => {
    const buckets = model.results.stage_esge?.[stage]?.buckets || {};
    const contributions = [
      [Number(buckets.environmental || 0) * 0.5, ESGE_COLORS.environmental],
      [Number(buckets.social || 0) * 0.2, ESGE_COLORS.social],
      [Number(buckets.governance || 0) * 0.2, ESGE_COLORS.governance],
      [Number(buckets.economic || 0) * 0.1, ESGE_COLORS.economic],
    ];
    const bx = x + 13 + index * ((w - 26) / stagesCount(STAGES_ORDER));
    let cursorY = baseY;
    contributions.forEach(([value, color]) => {
      const segment = (value / 10) * maxH;
      cursorY -= segment;
      rect(doc, bx, cursorY, barW, segment, { fillColor: color });
    });
    drawTextBox(doc, STAGE_SHORT[stage], bx - 4, baseY + 1, barW + 8, 7, { size: 4.1, color: COLORS.muted, align: "center", maxLines: 2, valign: "top" });
  });
  stroke(doc, COLORS.line);
  doc.setLineWidth(0.25);
  doc.line(x + 5, baseY, x + w - 5, baseY);
};

const stagesCount = (stages) => Math.max(1, stages.length);

const pageFive = (doc, model) => {
  sectionHeading(doc, 5, "Technical Appendix", "Stage sustainability overview and substage ESGE performance.");
  const panel = (y,h,title) => {
    rect(doc,12,y,186,h,{fillColor:COLORS.white,strokeColor:COLORS.line,radius:1});
    rect(doc,12,y,186,9,{fillColor:"#315849",radius:1});
    drawText(doc,title,16,y+6,{size:10,weight:"bold",color:COLORS.white});
  };
  panel(34,76,"Stage Sustainability Overview");
  drawText(doc,"Stage Sustainability Score and Criticality (out of 100)",16,48,{size:6.2,color:COLORS.muted});
  drawText(doc,"Score",152,48,{size:5.5,color:"#32654F"});
  drawText(doc,"Criticality",170,48,{size:5.5,color:"#719A87"});
  STAGES_ORDER.forEach((stage,i)=>{
    const x=15+i*36.5,base=100,chartH=34;
    drawTextBox(doc,STAGE_FULL[stage],x,52,34,8,{size:6.4,weight:"bold",align:"center",maxLines:2});
    [0,2,4,6,8,10].forEach(t=>{const y=base-t/10*chartH;
      stroke(doc,COLORS.line);doc.setLineWidth(.15);doc.line(x+5,y,x+34,y);
      drawText(doc,t * 10,x+3,y+1,{size:4.3,color:COLORS.muted,align:"right"});
    });
    [[Number(model.results.stage_si?.[stage]||0),x+8,"#32654F","Score"],[Number(model.results.stage_esge?.[stage]?.criticality||0),x+23,"#BFD7CC","Criticality"]].forEach(([value,bx,c,label])=>{
      const h=Math.max(0,Math.min(10,value))/10*chartH;
      if(h)rect(doc,bx,base-h,9,h,{fillColor:c,radius:.5});
      drawText(doc,score100(value, 2),bx+4.5,base-h-2,{size:5.6,weight:"bold",align:"center"});
      drawText(doc,label,bx+4.5,104,{size:4.2,color:COLORS.muted,align:"center"});
    });
  });
  panel(116,157,"Substage ESGE Performance");
  const dims=[["environmental","Environmental","#278634"],["social","Social","#2671ED"],["governance","Governance","#EF840C"],["economic","Economic","#D82938"]];
  dims.forEach(([key,label,c],i)=>{fill(doc,c);doc.circle(17+i*43,131,.8,"F");drawText(doc,label,19+i*43,132,{size:5.6,color:c});});
  rect(doc,15,136,180,8,{fillColor:"#EDF4F0"});
  drawText(doc,"Lifecycle stage",17,141,{size:5.4,weight:"bold"});
  drawText(doc,"Substage",43,141,{size:5.4,weight:"bold"});
  drawText(doc,"ESGE Score",174,141,{size:5.4,weight:"bold"});
  const plotX=77,plotW= 80;
  const rowH=9.5,top=144;
  [0,2,4,6,8,10].forEach(t=>{
    const x=plotX+t/10*plotW;
    drawText(doc,t * 10,x,141,{size:5,color:COLORS.muted,align:"center"});
    stroke(doc,COLORS.line);doc.setLineWidth(.15);doc.line(x,144,x,top+model.activeSubstages.length*rowH);
  });
  model.activeSubstages.forEach((stage,i)=>{
    const y=top+i*rowH,item=model.results.substage_esge?.[stage]||{};
    rect(doc,15,y,57,rowH,{fillColor:i%2?"#F5F9F7":"#EDF4F0"});
    stroke(doc,COLORS.line);doc.setLineWidth(.15);doc.line(15,y+rowH,195,y+rowH);
    drawTextBox(doc,SS_STAGE[stage],16,y,26,rowH,{size:4.8,weight:"bold",maxLines:2,padding:1});
    drawTextBox(doc,SS_FULL[stage].split(" - ").slice(1).join(" - "),42,y,29,rowH,{size:4.8,maxLines:2,padding:1});
    dims.forEach(([key,label,c],j)=>{
      const v=Number(item.buckets?.[key]||0),x=plotX+Math.max(0,Math.min(10,v))/10*plotW,dy=y+1.6+j*2.1;
      fill(doc,c);doc.circle(x,dy,.6,"F");drawText(doc,score100(v, 2),x+1.2,dy+.6,{size:4.2,color:c});
    });
    const v=Number(item.esge||0);
    rect(doc,174,y+3,13,3,{fillColor:"#E3E9E5"});
    if(v>0)rect(doc,174,y+3,Math.min(10,v)/10*13,3,{fillColor:"#32654F"});
    drawText(doc,score100(v, 2),194,y+5.5,{size:5.3,weight:"bold",align:"right"});
  });
  drawTextBox(doc,"Higher dimension scores indicate stronger sustainability performance. ESGE combines the four dimensions; criticality indicates intervention priority.",16,253,178,15,{size:5.7,color:COLORS.muted,maxLines:3});
  pageFooter(doc,5,"Stage and substage sustainability performance");
};

const pageSix = (doc, model) => {
  appendixHeading(doc, "B", "Normalized parameter evidence", "Twelve parameter scores with fixed gauges, status labels and aligned stage mini-bars.");
  const cardW = 44.1;
  const cardH = 35;
  model.parameterCards.forEach((card, index) => {
    const col = index % 4;
    const row = Math.floor(index / 4);
    const x = 12 + col * 47.3;
    const y = 34 + row * 37.7;
    rect(doc, x, y, cardW, cardH, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 1.6 });
    drawTextBox(doc, card.label, x + 2, y + 1.5, cardW - 4, 6, { size: 5.2, weight: "bold", align: "center", maxLines: 1 });
    drawSemicircleGauge(doc, x + cardW / 2, y + 19, 9.5, card.value, card.band.color);
    drawText(doc, card.value == null ? "N/A" : score100(card.value, 2), x + cardW / 2, y + 18.6, { size: 8.6, color: card.band.color, align: "center" });
    drawText(doc, "/ 100", x + cardW / 2, y + 22.2, { size: 3.8, color: COLORS.muted, align: "center" });
    drawPill(doc, card.band.label, x + cardW / 2 - 8, y + 23.6, 16, 4.6, card.band.color, card.band.tone, { size: 3.8 });
    const barBase = y + 32.3;
    const startX = x + 4;
    const slot = (cardW - 8) / Math.max(1, card.stageValues.length);
    card.stageValues.forEach((stage, stageIndex) => {
      const bx = startX + stageIndex * slot;
      const bh = Math.max(0.7, (Math.max(0, Math.min(10, stage.value)) / 10) * 5);
      drawText(doc, score100(stage.value, 1), bx + slot / 2, y + 28.4, { size: 3.4, color: scoreColor(stage.value), weight: "bold", align: "center" });
      rect(doc, bx + 1.2, barBase - bh, slot - 2.4, bh, { fillColor: scoreColor(stage.value), radius: 0.3 });
      drawText(doc, stage.label, bx + slot / 2, y + 34.1, { size: 2.8, color: COLORS.muted, align: "center" });
    });
  });

  rect(doc, 12, 150, 91, 67, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "DOMINANT PARAMETERS", 16, 158, { size: 5.6, color: COLORS.muted, weight: "bold" });
  STAGES_ORDER.forEach((stage, index) => {
    const col = index % 3;
    const row = Math.floor(index / 3);
    const x = 16 + col * 28;
    const y = 162 + row * 25;
    const dominance = model.dominance[stage] || { top: [], bottom: [] };
    rect(doc, x, y, 26, 22, { fillColor: COLORS.soft, strokeColor: COLORS.line, radius: 1 });
    drawText(doc, STAGE_FULL[stage], x + 1.5, y + 4.8, { size: 4.2, weight: "bold", width: 23, maxLines: 1 });
    dominance.top.slice(0, 2).forEach((item, itemIndex) => drawText(doc, `+ ${PL_SHORT[item.pk]} ${score100(item.adj, 1)}`, x + 1.5, y + 9 + itemIndex * 3.3, { size: 3.6, color: COLORS.green, width: 23, maxLines: 1 }));
    dominance.bottom.slice(0, 2).forEach((item, itemIndex) => drawText(doc, `- ${PL_SHORT[item.pk]} ${score100(item.adj, 1)}`, x + 1.5, y + 16 + itemIndex * 3.3, { size: 3.6, color: COLORS.red, width: 23, maxLines: 1 }));
  });

  rect(doc, 107, 150, 91, 67, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "POLLUTION DRIVERS", 111, 158, { size: 5.6, color: COLORS.muted, weight: "bold" });
  drawText(doc, "Weakest of GWP, pollution, ecosystem and water", 194, 158, { size: 4.2, color: COLORS.muted, align: "right" });
  STAGES_ORDER.forEach((stage, index) => {
    const driver = model.pollution[stage];
    const y = 162 + index * 10.2;
    const critical = Number(driver?.adj || 0) < 4;
    rect(doc, 111, y, 83, 8.4, { fillColor: critical ? COLORS.redSoft : COLORS.soft, strokeColor: critical ? "#F0B6B6" : COLORS.line, radius: 1 });
    drawTextBox(doc, STAGE_FULL[stage], 113, y, 24, 8.4, { size: 4.3, color: COLORS.muted, weight: "bold", maxLines: 1 });
    drawTextBox(doc, PL[driver?.pk] || "-", 138, y, 39, 8.4, { size: 4.5, weight: "bold", maxLines: 1 });
    drawTextBox(doc, score100(Number(driver?.adj || 0), 2), 178, y, 14, 8.4, { size: 5.1, color: scoreColor(Number(driver?.adj || 0)), weight: "bold", align: "right" });
  });

  rect(doc, 12, 222, 186, 44, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "PARAMETER VARIABILITY", 16, 230, { size: 5.6, color: COLORS.muted, weight: "bold" });
  STAGES_ORDER.forEach((stage, index) => {
    const value = model.variability[stage] || { min: 0, max: 0, spread: 0 };
    const y = 236 + index * 5.4;
    drawTextBox(doc, STAGE_FULL[stage], 16, y - 2.4, 39, 5, { size: 4.5, maxLines: 1 });
    rect(doc, 57, y - 1.2, 87, 2.4, { fillColor: COLORS.soft, radius: 1.2 });
    rect(doc, 57 + value.min * 8.7, y - 1.2, Math.max(0.8, (value.max - value.min) * 8.7), 2.4, { fillColor: value.spread >= 4 ? "#F29C86" : value.spread >= 2 ? "#F4BE72" : "#83B98A", radius: 1.2 });
    drawText(doc, `min ${score100(value.min, 2)}`, 149, y + 0.7, { size: 4.3, color: scoreColor(value.min) });
    drawText(doc, `max ${score100(value.max, 2)}`, 168, y + 0.7, { size: 4.3, color: scoreColor(value.max) });
    drawText(doc, `spread ${score100(value.spread, 2)}`, 194, y + 0.7, { size: 4.3, color: value.spread >= 4 ? COLORS.red : value.spread >= 2 ? COLORS.orange : COLORS.green, weight: "bold", align: "right" });
  });
  pageFooter(doc, 6, "Normalized parameter evidence");
};

const pageSeven = (doc, model) => {
  appendixHeading(doc, "C", "Parameter x substage matrix", "The heatmap uses the transparency-adjusted parameter scores from the calculation engine. Cell fills are drawn to exact table boundaries.");
  const groups = [model.activeSubstages.slice(0, 6), model.activeSubstages.slice(6)].filter((group) => group.length);
  groups.forEach((group, groupIndex) => {
    const y = groupIndex === 0 ? 39 : 153;
    drawText(doc, groupIndex === 0 ? "LIFECYCLE SUBSTAGES 01-06" : "LIFECYCLE SUBSTAGES 07-11", 15, y - 4.5, { size: 5.2, color: COLORS.muted, weight: "bold" });
    const columns = [
      { key: "parameter", label: "Parameter", width: 0.18 },
      ...group.map((stage) => ({ key: stage, label: SS_SHORT[stage], width: 0.82 / group.length, align: "center" })),
    ];
    const rows = PARAM_KEYS.map((parameter) => ({
      parameter: PL[parameter],
      ...Object.fromEntries(group.map((stage) => [stage, (model.results.parameter_scores?.[stage]?.[parameter]?.adjusted == null ? "N/A" : score100(model.results.parameter_scores[stage][parameter].adjusted, 1))])),
    }));
    rows.push({ parameter: "Parameter Avg.", ...Object.fromEntries(group.map((stage) => [stage, score100(Number(model.results.ssi?.[stage] || 0), 2)])) });
    drawTable(doc, {
      x: 12, y, width: 186, headerHeight: 8, rowHeight: 7.2, columns, rows,
      alternate: false, totalRows: new Set([rows.length - 1]), bodySize: 5.1, headerSize: 4.8,
      cellStyle: (row, column, rowIndex) => {
        if (rowIndex === rows.length - 1) return {};
        if (column.key === "parameter") return { weight: "bold", fillColor: COLORS.white };
        const value = Number(row[column.key]);
        return { fillColor: heatColor(value), align: "center" };
      },
    });
  });
  pageFooter(doc, 7, "Parameter x substage matrix");
};

const pageEight = (doc, model) => {
  appendixHeading(doc, "D", "Root causes and traceability", "For each underperforming substage (score < 50), the two weakest parameters are shown as the root causes.");
  const causes = model.causes;
  if (!causes.length) {
    rect(doc, 12, 38, 186, 25, { fillColor: COLORS.greenSoft, strokeColor: "#A8D5AD", radius: 2 });
    drawTextBox(doc, "No weak substages (all score >= 50). Root-cause analysis is not required.", 17, 38, 176, 25, { size: 7.2, color: COLORS.greenDark, weight: "bold" });
  } else {
    const gap = 2;
    const available = 228;
    const cardHeight = Math.min(27, (available - gap * (causes.length - 1)) / causes.length);
    causes.forEach((cause, index) => {
      const y = 37 + index * (cardHeight + gap);
      rect(doc, 12, y, 186, cardHeight, { fillColor: COLORS.redSoft, strokeColor: "#EBAAAA", radius: 2 });
      drawText(doc, cause.label, 16, y + 6.2, { size: 6.2, weight: "bold", width: 102, maxLines: 1 });
      drawText(doc, `Overall score: ${score100(Number(cause.score || 0), 2)}`, 194, y + 6.2, { size: 5.4, color: scoreColor(Number(cause.score || 0)), weight: "bold", align: "right" });
      const driverY = y + 8.7;
      const driverH = Math.max(8.5, cardHeight - 14.2);
      cause.drivers.slice(0, 2).forEach((driver, driverIndex) => {
        const x = 16 + driverIndex * 89;
        rect(doc, x, driverY, 86, driverH, { fillColor: COLORS.white, strokeColor: COLORS.line, radius: 1 });
        drawTextBox(doc, "ROOT CAUSE", x + 2, driverY, 23, driverH, { size: 4.5, color: COLORS.red, weight: "bold" });
        drawTextBox(doc, PL[driver.pk], x + 27, driverY, 43, driverH, { size: 5.1, maxLines: 2 });
        drawTextBox(doc, score100(Number(driver.adj || 0), 2), x + 71, driverY, 13, driverH, { size: 5.2, color: scoreColor(Number(driver.adj || 0)), weight: "bold", align: "right" });
      });
      if (cardHeight >= 23) {
        drawText(doc, `Weakness driven by ${cause.drivers.map((driver) => PL[driver.pk]).join(" and ")}. Targeted improvement lifts the entire substage.`, 16, y + cardHeight - 2.2, { size: 4.2, color: COLORS.muted, width: 176, maxLines: 1 });
      }
    });
  }
  pageFooter(doc, 8, "Root causes and traceability");
};

const pageNine = (doc, model) => {
  drawText(doc, "D. Root causes and traceability - BOM / raw inputs", 12, 19, { size: 9.2, weight: "bold" });
  stroke(doc, COLORS.aqua); doc.setLineWidth(0.4); doc.line(12, 22, 198, 22);
  drawText(doc, `BILL OF MATERIALS (${model.bom.length} COMPONENTS)`, 12, 29, { size: 5.5, color: COLORS.muted, weight: "bold" });
  const bomRows = model.bom.map((item) => ({
    material: item.material,
    country: item.country || "-",
    component: item.component || "-",
    qty: item.quantity ?? 1,
    unitWeight: Number(item.weight_kg || 0).toFixed(4),
    lineMass: Number(item.line_mass_kg ?? ((Number(item.quantity) || 1) * (Number(item.weight_kg) || 0))).toFixed(4),
    share: item.mass_fraction != null ? `${(Number(item.mass_fraction) * 100).toFixed(2)}%` : "-",
  }));
  const bomEnd = drawTable(doc, {
    x: 12, y: 32, width: 186, headerHeight: 8, rowHeight: 7.2,
    columns: [
      { key: "material", label: "Material", width: 0.18 },
      { key: "country", label: "Country", width: 0.10 },
      { key: "component", label: "Component", width: 0.31 },
      { key: "qty", label: "Qty", width: 0.08, align: "center" },
      { key: "unitWeight", label: "Unit weight (kg)", width: 0.13, align: "right" },
      { key: "lineMass", label: "Line mass (kg)", width: 0.12, align: "right" },
      { key: "share", label: "Mass share", width: 0.08, align: "right" },
    ], rows: bomRows, bodySize: 4.9, headerSize: 4.3,
    cellStyle: (row, column) => column.key === "material"
      ? { color: COLORS.green, weight: "bold" }
      : column.key === "share" ? { color: COLORS.green, weight: "bold" } : {},
  });

  const rawY = Math.max(84, bomEnd + 10);
  drawText(doc, "RAW STAGE INPUTS - AGGREGATED ACROSS SUBSTAGES", 12, rawY - 3.5, { size: 5.5, color: COLORS.muted, weight: "bold" });
  const displayedStageKeys = Object.keys(model.data.stage_inputs || {}).filter((key) => !HIDDEN_FROM_TABLE_SUBSTAGES.includes(key));
  const rawRows = PARAM_KEYS.map((parameter) => {
    const aggregate = model.aggregates[parameter] || { value: 0, method: PARAM_AGG[parameter] };
    return {
      parameter: PL[parameter], unit: advancedUnit(model.results.unit_summary?.audit?.canonical_units?.[parameter] || PARAM_UNITS[parameter]),
      ...Object.fromEntries(displayedStageKeys.map((stage) => [stage, model.data.stage_inputs?.[stage]?.[parameter] ?? "-"])),
      total: aggregate.value == null ? "Unavailable / separate indicators" : `${aggregate.method === "sum" ? "SUM" : "AVG"} ${Number(aggregate.value).toFixed(2)} ${aggregate.unit || PARAM_UNIT_SHORT[parameter]}`,
    };
  });
  const firstWidth = 0.09;
  const unitWidth = 0.11;
  const stageWidth = 0.69 / Math.max(1, displayedStageKeys.length);
  const rawEnd = drawTable(doc, {
    x: 12, y: rawY, width: 186, headerHeight: 9, rowHeight: 6,
    columns: [
      { key: "parameter", label: "Parameter", width: firstWidth },
      { key: "unit", label: "Unit", width: unitWidth },
      ...displayedStageKeys.map((stage) => ({ key: stage, label: SS_SHORT[stage] || stage.replace(/_/g, " "), width: stageWidth, align: "center" })),
      { key: "total", label: "Total / avg", width: 0.11, align: "center" },
    ], rows: rawRows, bodySize: 3.7, headerSize: 3.8,
    cellStyle: (row, column) => column.key === "parameter"
      ? { weight: "bold" }
      : column.key === "unit" ? { color: COLORS.muted, size: 3.4, maxLines: 2 }
        : column.key === "total" ? { fillColor: "#EAF2EF", weight: "bold", size: 3.5, maxLines: 2 } : { align: "center" },
  });
  drawText(doc, "Aggregation follows the saved method: physical quantities are separate from scores. Advanced 2.0 sums GWP, water, pollution points and allocated cost. Time is mean stage duration, not total elapsed time.", 12, rawEnd + 5.5, { size: 4.7, color: COLORS.muted, width: 186, maxLines: 2 });
  const audit = model.results.unit_summary?.audit;
  const noteY = rawEnd + 14;
  rect(doc, 12, noteY, 186, 16, { fillColor: COLORS.soft, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "UNIT VALIDATION AND NORMALIZATION", 16, noteY + 5.5, { size: 4.8, color: COLORS.muted, weight: "bold" });
  drawText(doc, `${audit?.issues?.length || 0} issues | functional unit: ${audit?.functional_unit?.label || model.data.functional_unit || "-"} | BOM mass: ${Number(audit?.bom_total_mass_kg || model.productMass).toFixed(4)} kg | mass used: ${Number(audit?.effective_product_mass_kg || model.productMass).toFixed(4)} kg`, 16, noteY + 11.5, { size: 4.8, color: (audit?.issues?.length || 0) ? COLORS.orange : COLORS.green, weight: "bold", width: 176, maxLines: 2 });
  rect(doc, 12, noteY + 20, 186, 15, { fillColor: COLORS.soft, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "TRANSPARENCY SETTING", 16, noteY + 25.5, { size: 4.8, color: COLORS.muted, weight: "bold" });
  drawText(doc, "Per-substage / per-parameter transparency applied. Average reliability is reflected in Section E.", 16, noteY + 31, { size: 5, width: 176, maxLines: 1 });
  rect(doc, 12, noteY + 39, 186, 18, { fillColor: COLORS.soft, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "BENCHMARK SOURCES", 16, noteY + 44.5, { size: 4.8, color: COLORS.muted, weight: "bold" });
  drawText(doc, `Material stages: selected country actuals versus world benchmarks; Missing applicable material data receives an assumed zero score; it is not a measured zero. Product stages: best/worst benchmarks for ${model.data.product_type || "the selected product type"}. All scoring remains deterministic and auditable.`, 16, noteY + 50, { size: 4.7, width: 176, maxLines: 2 });
  pageFooter(doc, 9, "BOM and raw inputs");
};

const pageTen = (doc, model) => {
  appendixHeading(doc, "E", "Data transparency by substage", "Average evidence transparency across applicable parameters. Coloured bars use the full 0-100 scale and are drawn as native PDF vectors.");
  const rows = model.activeSubstages.map((stage) => {
    const value = model.substageTransparency[stage];
    const band = transparencyBand(value);
    return { stage: SS_STAGE[stage], substage: SS_FULL[stage], value, score: `${score100(value, 2)} / 100`, band: band.label, bandInfo: band };
  });
  rows.push({ stage: "Overall average (all substages x parameters)", substage: "", value: model.avgTransparency, score: `${score100(model.avgTransparency, 2)} / 100`, band: transparencyBand(model.avgTransparency).label, bandInfo: transparencyBand(model.avgTransparency), total: true });
  const x = 12;
  const y = 38;
  const width = 186;
  const columns = [
    { key: "stage", label: "Lifecycle stage", width: 0.18 },
    { key: "substage", label: "Substage", width: 0.29 },
    { key: "score", label: "Avg. transparency", width: 0.18, align: "center" },
    { key: "band", label: "Evidence band", width: 0.21, align: "center" },
    { key: "visual", label: "Visual", width: 0.14, align: "center" },
  ];
  const widths = columns.map((column) => column.width * width);
  let cursorX = x;
  columns.forEach((column, index) => {
    rect(doc, cursorX, y, widths[index], 10, { fillColor: COLORS.ink });
    drawTextBox(doc, column.label, cursorX, y, widths[index], 10, { size: 4.8, color: COLORS.white, weight: "bold", align: column.align || "left", padding: 1.5, maxLines: 2 });
    cursorX += widths[index];
  });
  let cursorY = y + 10;
  rows.forEach((row, rowIndex) => {
    const h = 10.5;
    const base = row.total ? "#48665C" : rowIndex % 2 ? COLORS.soft : COLORS.white;
    rect(doc, x, cursorY, width, h, { fillColor: base });
    cursorX = x;
    columns.forEach((column, columnIndex) => {
      stroke(doc, COLORS.line); doc.setLineWidth(0.2); doc.rect(cursorX, cursorY, widths[columnIndex], h, "S");
      if (column.key === "visual") {
        drawProgress(doc, cursorX + 4, cursorY + 4.1, widths[columnIndex] - 8, 2.4, row.value, row.bandInfo.color, row.total);
      } else if (column.key === "band" && !row.total) {
        drawPill(doc, row.band, cursorX + (widths[columnIndex] - 24) / 2, cursorY + 2.1, 24, 6.3, row.bandInfo.color, row.bandInfo.tone, { size: 4.2 });
      } else {
        const value = row[column.key];
        drawTextBox(doc, value, cursorX, cursorY, widths[columnIndex], h, {
          size: column.key === "score" ? 5.5 : 5.1,
          color: row.total ? COLORS.white : column.key === "score" ? row.bandInfo.color : COLORS.ink,
          weight: row.total || column.key === "stage" || column.key === "score" ? "bold" : "normal",
          align: column.align || "left", padding: 1.5, maxLines: 2,
        });
      }
      cursorX += widths[columnIndex];
    });
    cursorY += h;
  });
  rect(doc, 12, cursorY + 10, 186, 30, { fillColor: COLORS.mint, strokeColor: COLORS.line, radius: 2 });
  drawText(doc, "TRANSPARENCY SCALE", 17, cursorY + 18, { size: 5.5, color: COLORS.muted, weight: "bold" });
  const legend = [
    ["High / declared", COLORS.greenDark, COLORS.greenSoft, "80-100"],
    ["Medium / self-reported", "#EF6C00", COLORS.orangeSoft, "40 to <80"],
    ["Low / estimated", "#C62828", "#FFEBEE", "0 to <40"],
  ];
  legend.forEach(([label, color, tone, range], index) => {
    const lx = 17 + index * 58;
    drawPill(doc, label, lx, cursorY + 21, 40, 7, color, tone, { size: 4.4 });
    drawTextBox(doc, range, lx + 42, cursorY + 21, 13, 7, { size: 4.6, color: COLORS.muted, weight: "bold", align: "right" });
  });
  pageFooter(doc, 10, "Data transparency by substage");
};

const appendMethodAudit = (doc, model) => {
  if (!model.results.methodology_version) return;
  let y = 0;
  const newPage = () => { doc.addPage('a4', 'portrait'); appendixHeading(doc, 'F', 'Methods, evidence and exclusions', model.results.methodology_version); y = 36; };
  const paragraph = (text, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8);
    const lines = doc.splitTextToSize(ascii(text), 178);
    for (const line of lines) {
      if (!y || y > 273) newPage();
      drawText(doc, line, 16, y, {size:8, weight:bold ? 'bold' : 'normal', color:COLORS.ink}); y += 4.2;
    }
    y += 2;
  };
  paragraph(coverageDescription(model.results), true);
  paragraph(model.results.material_evidence_policy);
  paragraph(evidenceSummary(model.results));
  paragraph(stageScoreNote(model.results));
  if (model.results.aggregation?.method === 'process_b') {
    paragraph('Process B: ESGE component = 100 - sqrt(mean((100 - ESGE)^2)). Combined score = 0.5 x mean performance + 0.5 x ESGE component.', true);
    for (const [label, a] of processBBreakdown(model.results)) paragraph(`${label}: mean performance ${score100(a.mean_performance,2)}; ESGE component ${score100(a.esge_component,2)}; combined ${score100(a.score,2)} /100.`);
  }
  paragraph('Displayed sustainability and evidence scores use 0-100 (engine score multiplied by 10). Physical inputs retain their declared units.');
  for (const note of model.results.unit_summary?.aggregation_notes || []) paragraph(note);
  for (const p of ['circularity','pollution']) for (const item of model.results.unit_summary?.parameter_aggregates?.[p]?.indicators || []) paragraph(`${SS_FULL[item.substage]} / ${PL[p]}: ${formatValue(item.value)} ${item.unit}; ${item.method}`);

  if (model.results.unit_basis_version) {
    paragraph(`Unit basis: ${model.results.functional_unit}. Finished-product mass: ${model.results.product_mass_kg} kg. Physical inputs and benchmarks are normalized per kg using their respective source reference masses. Percentages, scores and durations are not mass-scaled.`, true);
    for (const c of model.results.unit_summary?.audit?.basis_conversions || []) paragraph(`${c.location} / ${PL[c.parameter]}: ${c.source_value} ${c.source_unit}${c.reference_mass_kg == null ? " converted to" : ` divided by ${c.reference_mass_kg} kg =`} ${c.normalized_value} ${c.normalized_unit}`);
  }
  paragraph('Product ecosystem inputs measure management performance (higher is better). Material ecosystem data measures damage (lower is better); raw values are not averaged together.');
  for (const [ss, params] of Object.entries(model.results.exclusions || {}))
    for (const [p, reason] of Object.entries(params)) paragraph(`N/A: ${SS_FULL[ss]} / ${PL[p]}: ${reason}`);
  for (const [ss, params] of Object.entries(model.results.calculator_results || {})) {
    for (const [p, result] of Object.entries(params)) {
      paragraph(`${SS_FULL[ss]} / ${PL[p]}: ${formatValue(result.value)} ${result.unit}`, true);
      paragraph(`${result.method}: ${result.formula}`);
      for (const [key, value] of Object.entries(result.inputs)) {
        if (Array.isArray(value)) value.forEach(row => paragraph(`${key}: ${Object.entries(row).map(([k,v])=>`${k.replace(/_/g,' ')}: ${v}`).join('; ')}`));
        else paragraph(`${key.replace(/_/g,' ')}: ${value}`);
      }
    }
  }
  if(model.results.automatic_assumptions?.length) {
    paragraph('Automatic conservative defaults: missing applicable input data receives 0/100; physical quantities remain unknown.',true);
    for(const a of model.results.automatic_assumptions) paragraph(`${SS_FULL[a.substage]} / ${PL[a.parameter]}: ${a.reason}`);
  }
  paragraph('Material evidence used', true);
  for (const [ss, params] of Object.entries(model.results.parameter_scores || {}))
    for (const [p, result] of Object.entries(params))
      if (result.sources) paragraph(`${SS_FULL[ss]} / ${PL[p]}: ${result.sources.map(s=>`${s.country} / ${s.material} (${score100(s.level)}/100): ${s.source}`).join('; ')}`);
};

export async function buildAdvancedReportPdf(data, countryData = {}) {
  if (!data?.results) throw new Error("Assessment results are required to generate the PDF report.");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true, putOnlyUsedFonts: true });
  doc.setProperties({
    title: `TOSS Advanced - ${ascii(data.product_name || "Assessment")}`,
    subject: "TOSS Advanced Assessment Report",
    author: "TOSS - Product Sustainability Index",
    creator: "TOSS deterministic PDF renderer",
  });
  const model = buildModel(data, countryData);
  const pages = [reportCover, pageOne, pageTwo, pageThree, pageFour, pageFive, pageSix, pageSeven, pageEight, pageNine, pageTen];
  pages.forEach((drawPage, index) => {
    if (index) doc.addPage("a4", "portrait");
    fill(doc, COLORS.white);
    doc.rect(0, 0, PAGE.w, PAGE.h, "F");
    drawPage(doc, model);
  });
  appendMethodAudit(doc, model);
  const total = doc.getNumberOfPages();
  for (let page = 2; page <= total; page += 1) {
    doc.setPage(page);
    rect(doc, PAGE.right - 18, 285, 20, 7, {fillColor:COLORS.white});
    drawText(doc, `${page} / ${total}`, PAGE.right, 289.2, {size:6.4,color:COLORS.muted,align:'right'});
  }
  return doc;
}

export async function exportAdvancedReportToPdf(data, countryData, filename) {
  const doc = await buildAdvancedReportPdf(data, countryData);
  const bytes = doc.output("arraybuffer");
  if (!bytes || bytes.byteLength < 1000) throw new Error("The generated PDF is unexpectedly empty.");
  doc.save(filename);
}

